import React, { Component, createRef } from 'react';

export interface ParallaxData {
  imageSrc: string;
  naturalWidth: number;
  naturalHeight: number;
  speed?: number;  // default 0.2
  bleed?: number;  // default 10
}

interface ParallaxProps extends ParallaxData {}

type ParallaxMode = 'hidden' | 'static' | 'parallax';

interface ParallaxState {
  mode: ParallaxMode;
}

/**
 * Ermittelt die effektive Fensterbreite zuverlässig, auch wenn
 * initial-scale=0.75 im Viewport-Meta-Tag window.innerWidth aufbläht.
 * - window.outerWidth = echte OS-Fenstergröße (nicht durch initial-scale beeinflusst)
 * - window.innerWidth = CSS-Viewport (kleiner bei DevTools Responsive Mode)
 * Math.min liefert in beiden Szenarien den korrekten Wert.
 */
function getEffectiveWidth(): number {
  if (typeof window === 'undefined') return 9999;
  return Math.min(window.innerWidth, window.outerWidth || window.innerWidth);
}

function getMode(): ParallaxMode {
  const w = getEffectiveWidth();
  if (w <= 600) return 'hidden';
  if (w <= 900) return 'static';
  return 'parallax';
}

/**
 * Parallax banner component - ports the logic of parallax.js v1.5.0 to React
 * without jQuery.
 *
 * Modes (responsive):
 *   <= 600px  -> hidden (display: none)
 *   <= 900px  -> static background-image (no scroll effect)
 *   >  900px  -> full parallax animation
 */
class Parallax extends Component<ParallaxProps, ParallaxState> {
  private containerRef = createRef<HTMLDivElement>();
  private sliderRef = createRef<HTMLImageElement>();
  private rafId: number | null = null;
  private bodyObserver: ResizeObserver | null = null;

  // Layout cache - recomputed on mount, resize, load, or document layout shift
  private boxWidth = 0;
  private boxHeight = 0;
  private boxOffsetTop = 0;
  private imageWidth = 0;
  private imageHeight = 0;
  private offsetLeft = 0;

  constructor(props: ParallaxProps) {
    super(props);
    this.state = { mode: getMode() };
  }

  componentDidMount(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.onQueryChange, { passive: true });
    }
    if (this.state.mode === 'parallax') {
      this.initParallax();
    }
  }

  private onQueryChange = (): void => {
    const nextMode = getMode();
    const prevMode = this.state.mode;
    if (nextMode === prevMode) return;
    this.setState({ mode: nextMode }, () => {
      if (prevMode === 'parallax') this.destroyParallax();
      if (nextMode === 'parallax') this.initParallax();
    });
  };

  private initParallax(): void {
    this.computeLayout();
    this.scheduleFrame();
    window.addEventListener('scroll', this.scheduleFrame, { passive: true });
    window.addEventListener('resize', this.onResize);
    window.addEventListener('load', this.onResize);
    if (typeof ResizeObserver !== 'undefined') {
      this.bodyObserver = new ResizeObserver(this.onResize);
      this.bodyObserver.observe(document.body);
    }
  }

  private destroyParallax(): void {
    window.removeEventListener('scroll', this.scheduleFrame);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('load', this.onResize);
    this.bodyObserver?.disconnect();
    this.bodyObserver = null;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  componentWillUnmount(): void {
    window.removeEventListener('resize', this.onQueryChange);
    this.destroyParallax();
  }

  private onResize = (): void => {
    this.computeLayout();
    this.scheduleFrame();
  };

  private scheduleFrame = (): void => {
    if (this.rafId !== null) return;
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.applyFrame();
    });
  };

  private computeLayout(): void {
    const container = this.containerRef.current;
    const slider = this.sliderRef.current;
    if (!container) return;

    const { bleed = 10, speed = 0.2, naturalWidth, naturalHeight } = this.props;
    const aspectRatio = naturalWidth / naturalHeight;

    this.boxWidth = container.offsetWidth;
    this.boxHeight = container.offsetHeight + bleed * 2;

    this.boxOffsetTop = container.getBoundingClientRect().top + window.scrollY - bleed;
    const boxOffsetBottom = this.boxOffsetTop + this.boxHeight;

    const winHeight = window.innerHeight;

    const minOffset = Math.max(boxOffsetBottom - winHeight, 0);
    const imageHeightMin = (this.boxHeight + (this.boxOffsetTop - minOffset) * (1 - speed)) | 0;

    if (imageHeightMin * aspectRatio >= this.boxWidth) {
      this.imageWidth = (imageHeightMin * aspectRatio) | 0;
      this.imageHeight = imageHeightMin;
      this.offsetLeft = -((this.imageWidth - this.boxWidth) / 2 | 0);
    } else {
      this.imageWidth = this.boxWidth;
      this.imageHeight = (this.boxWidth / aspectRatio) | 0;
      this.offsetLeft = 0;
    }

    if (slider) {
      slider.style.width = `${this.imageWidth}px`;
      slider.style.height = `${this.imageHeight}px`;
    }
  }

  private applyFrame(): void {
    const container = this.containerRef.current;
    const slider = this.sliderRef.current;
    if (!container || !slider) return;

    const { bleed = 10, speed = 0.2 } = this.props;
    const scrollTop = window.scrollY;
    const boxOffsetBottom = this.boxOffsetTop + this.boxHeight;

    if (boxOffsetBottom <= scrollTop || this.boxOffsetTop > scrollTop + window.innerHeight) {
      slider.style.visibility = 'hidden';
      return;
    }

    const mirrorTop = this.boxOffsetTop - scrollTop;
    const sliderTop = -mirrorTop * (1 - speed) - bleed;

    slider.style.transform = `translate3d(${this.offsetLeft}px, ${sliderTop}px, 0)`;
    slider.style.visibility = 'visible';
  }

  render(): React.ReactNode {
    const { imageSrc } = this.props;
    const { mode } = this.state;

    if (mode === 'hidden') {
      return null;
    }

    if (mode === 'static') {
      return (
        <div
          className="parallax-container"
          style={{
            backgroundImage: `url(${imageSrc})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
          }}
        />
      );
    }

    return (
      <div
        ref={this.containerRef}
        className="parallax-container"
        style={{ position: 'relative', overflow: 'hidden' }}
      >
        <img
          ref={this.sliderRef}
          src={imageSrc}
          alt=""
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            maxWidth: 'none',
            display: 'block',
          }}
        />
      </div>
    );
  }
}

export default Parallax;
